import { Fragment, useMemo } from 'react'
import { AppLink } from '@/components/ui/app-link'
import { Badge } from '@/components/ui/badge'
import {
  buildTimelineAxis,
  formatDateRange,
  monthsBetween,
  utcMs,
  type TimelineMonth,
} from '@/lib/utils/timeline-axis'
import { weekGroupHeader } from '@/lib/utils/availability-utils'
import type { Granularity } from '@/types/ui/resource-availability'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'

export interface EngagementTimelineRow {
  engagementId: string
  engagementName?: string
  /** Engagement-level CoreInfo dates. Drives the lighter "engagement duration" overlay. */
  engagementStart: string // ISO yyyy-MM-dd
  engagementEnd: string
  /** Worker's actual first/last day on the engagement. Falls back to engagement dates when null. */
  workerStart: string | null
  workerEnd: string | null
  /** Worker's BudgetedHoursPerDay for this engagement. Shown as a chip alongside the name. */
  budgetedHoursPerDay?: number
  /** The chip text when scheduled changes vary the hours over the worker's time here, e.g. "8→4h/day". */
  budgetedHoursLabel?: string
}

export type { TimelineMonth }

interface EngagementTimelineWaterfallProps {
  rows: EngagementTimelineRow[]
  /** Axis buckets — monthly or weekly depending on the section's granularity toggle. */
  months: TimelineMonth[]
  /** Bucket granularity. Drives a two-tier header (month names spanning week columns)
   *  when weekly, mirroring the team waterfall on the dashboard. Defaults to 'monthly'. */
  granularity?: Granularity
}

// Wide enough to fit a 25–30 char name plus a chip row of BHPD · length · date range and
// (optionally) the "ends-soon" badge. Long names ellipsis via the link's truncate.
const LABEL_COL_WIDTH_REM = 18
// Bar styling mirrors the team waterfall on the dashboard so the two views read the same:
// a thin 9px bar centred vertically in the row via flex-col + justify-around. Using the
// same pixel value keeps visual rhythm consistent across the heatmap → team page → drill-
// down hand-off (a user clicking into a worker shouldn't see the bar pop to a new height).
const SUB_BAR_HEIGHT_PX = 9
// Neutral grey for the engagement-window overlay — same shade the team waterfall uses,
// so a worker drilldown feels like a zoomed-in slice of the team chart rather than a
// different-looking widget.
const ENGAGEMENT_WINDOW_BG = 'bg-slate-300/70'

/**
 * Gantt-style engagement waterfall: each engagement is one row with two overlaid bars
 * over a shared time axis:
 *   • a lighter "engagement duration" bar covering the engagement's overall window
 *   • a darker "worker active" bar showing when the worker is actually on the engagement
 *
 * Layout mirrors the team-page waterfall on the dashboard — scrollable container with
 * sticky-left label column, two-tier header (year + month for monthly, month + day-range
 * for weekly), per-cell minmax floors so columns stay legible across wide windows.
 * Granularity flips between monthly and weekly buckets without refetching.
 */
export function EngagementTimelineWaterfall({
  rows,
  months,
  granularity = 'monthly',
}: EngagementTimelineWaterfallProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.timeline
  const tHeatmap = dictionary?.resourceAvailability?.heatmap

  // Engagements running past the last visible bucket clip at the right edge — the chip
  // row in the engagement label already carries the full date range.
  const axis = useMemo(() => buildTimelineAxis(months, null), [months])

  const isWeekly = granularity === 'weekly'

  // In weekly mode, group consecutive cells by their start-of-month so the top header
  // tier renders one month name spanning the weeks of that month.
  interface WeekMonthGroup {
    yearMonth: string
    startIso: string
    startCol: number // 1-based axis index of first cell
    endCol: number   // 1-based axis index of last cell (inclusive)
    isCurrentMonth: boolean
  }
  const monthGroups: WeekMonthGroup[] = useMemo(() => {
    if (!isWeekly) return []
    const groups: WeekMonthGroup[] = []
    axis.cells.forEach((c, i) => {
      const ym = c.startIso.slice(0, 7)
      const last = groups[groups.length - 1]
      if (last && last.yearMonth === ym && last.endCol === i) {
        last.endCol = i + 1
        if (c.isCurrentMonth) last.isCurrentMonth = true
      } else {
        groups.push({
          yearMonth: ym,
          startIso: c.startIso,
          startCol: i + 1,
          endCol: i + 1,
          isCurrentMonth: c.isCurrentMonth,
        })
      }
    })
    return groups
  }, [axis.cells, isWeekly])

  // Year groups span every cell of the same calendar year — mirrors the team waterfall's
  // year tier so multi-year windows read unambiguously.
  interface YearGroup {
    year: string
    startCol: number
    endCol: number
  }
  const yearGroups: YearGroup[] = useMemo(() => {
    const groups: YearGroup[] = []
    axis.cells.forEach((c, i) => {
      const year = c.startIso.slice(0, 4)
      const last = groups[groups.length - 1]
      if (last && last.year === year && last.endCol === i) {
        last.endCol = i + 1
      } else {
        groups.push({ year, startCol: i + 1, endCol: i + 1 })
      }
    })
    return groups
  }, [axis.cells])

  // Axis indexes that mark the first cell of a new year (after the first year). Drives a
  // heavier left border on every header tier AND the per-row axis-cell backgrounds, so
  // the year divider reads as one continuous line through the chart.
  const yearBoundaryAxisIdx: Set<number> = useMemo(() => {
    const set = new Set<number>()
    for (let i = 1; i < yearGroups.length; i++) {
      set.add(yearGroups[i].startCol - 1)
    }
    return set
  }, [yearGroups])

  if (rows.length === 0 || axis.cells.length === 0) return null

  // Sort by worker actual start (or engagement start), longest first — earliest, longest
  // commitments float to the top so the visual flow reads left-to-right.
  const sortedRows = [...rows].sort((a, b) => {
    const as = utcMs(a.workerStart ?? a.engagementStart)
    const bs = utcMs(b.workerStart ?? b.engagementStart)
    if (as !== bs) return as - bs
    const ae = utcMs(a.workerEnd ?? a.engagementEnd)
    const be = utcMs(b.workerEnd ?? b.engagementEnd)
    return be - ae
  })

  // Calendar month-start of today, used to flag rows ending in the current month. Derived
  // from `new Date()` rather than `axis.currentMonthIso` because the latter is the start of
  // the *current bucket* — in weekly mode that's the current week's Monday, which can fall
  // in the prior or next calendar month around month boundaries and produce wrong "ending
  // soon" results.
  const today = new Date()
  const currentMonthStartIso = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-01`

  // Per-cell minimum width. Match the team waterfall's per-granularity floors so columns
  // stay legible — 80px monthly, 100px weekly.
  const cellMinPx = isWeekly ? 100 : 80
  const cellTemplate = axis.cells
    .map((_, i) => `minmax(${cellMinPx}px, ${axis.cellWidthsFr[i]}fr)`)
    .join(' ')
  const tableTemplate = `${LABEL_COL_WIDTH_REM}rem ${cellTemplate}`

  const headerRows = 2
  const rowOffset = headerRows + 1
  // Monthly: row 1 is year tier, row 2 is per-column month name.
  // Weekly: row 1 is month-with-year-suffix tier, row 2 is per-column day range. Year tier
  // is intentionally skipped — the inline ’YY suffix carries year context more reliably
  // when a wide year span would scroll its center label off-screen.
  const monthTierRow = isWeekly ? 1 : 2

  return (
    <div className="rounded-md border bg-card">
      <div className="overflow-x-auto">
        <div className="grid pb-3 pr-4 min-w-fit" style={{ gridTemplateColumns: tableTemplate }}>
          {/* ---- Engagement label header (rowSpans the full header) ---- */}
          <div
            className="pl-4 pr-3 pb-1 flex items-end sticky left-0 bg-card z-20 shadow-[1px_0_0_0_rgb(0_0_0_/_0.05)]"
            style={{ gridColumn: 1, gridRow: `1 / ${headerRows + 1}` }}
          >
            <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
              {t?.engagementHeader || 'Engagement'}
            </span>
          </div>

          {/* ---- Year tier — monthly only ---- */}
          {!isWeekly && yearGroups.map((g, gi) => (
            <div
              key={`y-${g.year}-${g.startCol}`}
              className={cn(
                'flex items-end justify-center text-[11px] tracking-wide font-semibold text-muted-foreground pt-3 pb-1.5',
                gi > 0 && 'border-l-2 border-foreground/30'
              )}
              style={{ gridColumn: `${g.startCol + 1} / ${g.endCol + 2}`, gridRow: 1 }}
            >
              {g.year}
            </div>
          ))}

          {/* ---- Month tier (weekly): month-with-year-suffix spans the weeks of that month ---- */}
          {isWeekly && monthGroups.map((g, gi) => {
            const startAxisIdx = g.startCol - 1
            const isYearBoundary = yearBoundaryAxisIdx.has(startAxisIdx)
            const groupCount = g.endCol - g.startCol + 1
            const { label, yearSuffix } = weekGroupHeader(g.startIso, groupCount)
            return (
              <div
                key={`mg-${g.yearMonth}-${g.startCol}`}
                className={cn(
                  'text-center text-[11px] uppercase tracking-wide font-semibold text-muted-foreground pt-3 pb-1.5',
                  gi > 0 && (isYearBoundary
                    ? 'border-l-2 border-foreground/30'
                    : 'border-l border-foreground/15'),
                  g.isCurrentMonth && 'text-foreground'
                )}
                style={{ gridColumn: `${g.startCol + 1} / ${g.endCol + 2}`, gridRow: monthTierRow }}
              >
                {label}
                {yearSuffix && (
                  <span className="ml-1 text-[10px] font-normal text-muted-foreground/70 normal-case tracking-normal">
                    ’{yearSuffix}
                  </span>
                )}
                {g.isCurrentMonth && (
                  <span className="ml-1 text-[8px] opacity-60 normal-case tracking-normal">
                    {tHeatmap?.nowLabel || 'NOW'}
                  </span>
                )}
              </div>
            )
          })}

          {/* ---- Bottom header tier: per-cell labels (day-range in weekly, month name in monthly). ---- */}
          {axis.cells.map((cell, i) => {
            const isYearBoundary = yearBoundaryAxisIdx.has(i)
            const showNow = cell.isCurrentMonth
            return (
              <div
                key={`h-${cell.startIso}`}
                className={cn(
                  'text-[10px] uppercase tracking-wide text-muted-foreground text-center px-0.5 pb-1',
                  i > 0 && (isYearBoundary
                    ? 'border-l-2 border-foreground/30'
                    : 'border-l border-foreground/10'),
                  cell.isCurrentMonth && !isWeekly && 'text-foreground font-semibold',
                  isWeekly && 'tracking-normal text-[10px] tabular-nums'
                )}
                style={{ gridColumn: i + 2, gridRow: 2 }}
              >
                {cell.label}
                {showNow && !isWeekly && (
                  <div className="text-[8px] opacity-60 normal-case tracking-normal">
                    {tHeatmap?.nowLabel || 'NOW'}
                  </div>
                )}
              </div>
            )
          })}

          {/* ---- Engagement rows ---- */}
          {sortedRows.map((row, rowIdx) => {
            const lighter = axis.rangePct(row.engagementStart, row.engagementEnd)
            const workerStartIso = row.workerStart ?? row.engagementStart
            const workerEndIso = row.workerEnd ?? row.engagementEnd
            const darker = axis.rangePct(workerStartIso, workerEndIso)

            const monthsLeftFromCurrent = monthsBetween(currentMonthStartIso, workerEndIso)
            const isEndingSoon = monthsLeftFromCurrent === 0

            const dateRangeLabel = formatDateRange(workerStartIso, workerEndIso)
            const lengthMonths = monthsBetween(workerStartIso, workerEndIso) + 1
            const lengthLabel = lengthMonths <= 1
              ? (t?.monthSingular || '1 mo')
              : (t?.monthPlural || '{n} mos').replace('{n}', String(lengthMonths))

            const gridRow = rowIdx + rowOffset

            return (
              <Fragment key={row.engagementId}>
                <div
                  className="border-t border-foreground/10"
                  style={{ gridColumn: '1 / -1', gridRow }}
                  aria-hidden="true"
                />

                {/* Engagement label cell — sticky-left, mirrors the team waterfall pattern. */}
                <div
                  className="pl-4 pr-3 py-2 min-w-0 sticky left-0 bg-card z-10 shadow-[1px_0_0_0_rgb(0_0_0_/_0.05)]"
                  style={{ gridColumn: 1, gridRow }}
                  title={row.engagementName ?? `Engagement ${row.engagementId}`}
                >
                  <AppLink
                    to={`/${row.engagementId}`}
                    className="block w-full truncate text-xs font-medium leading-tight text-left hover:underline hover:text-blue-700 focus-visible:outline-none focus-visible:underline"
                  >
                    {row.engagementName ?? `Engagement ${row.engagementId}`}
                  </AppLink>
                  <div className="flex items-baseline gap-1.5 mt-0.5 leading-tight min-w-0">
                    <span className="text-[10px] text-muted-foreground tabular-nums truncate">
                      {[
                        row.budgetedHoursLabel
                          ?? (row.budgetedHoursPerDay !== undefined ? `${row.budgetedHoursPerDay}h/day` : null),
                        lengthLabel,
                        dateRangeLabel,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {isEndingSoon && (
                      <Badge
                        variant="secondary"
                        className="text-[9px] bg-orange-50 text-orange-700 shrink-0"
                      >
                        {t?.endingSoonBadge || 'ending soon'}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Per-cell axis backgrounds (dividers). Year-boundary cells get a heavier
                    border so the year divider runs continuously top-to-bottom. */}
                {axis.cells.map((cell, i) => {
                  const isYearBoundary = yearBoundaryAxisIdx.has(i)
                  return (
                    <div
                      key={`${row.engagementId}-${cell.startIso}`}
                      className={cn(
                        i > 0 && (isYearBoundary ? 'border-l-2' : 'border-l'),
                        isYearBoundary ? 'border-foreground/30' : 'border-foreground/10'
                      )}
                      style={{ gridColumn: i + 2, gridRow }}
                      aria-hidden="true"
                    />
                  )
                })}

                {/* Bar overlay. Mirrors the team waterfall's pattern (flex-col + justify-
                    around) so the bar sits at a consistent height in the row whether it has
                    one bar (drill-down) or many (team page). The darker bar is clickable —
                    the engagement-name link in the label column is the primary affordance,
                    but a clickable bar is a familiar second target on Gantt-style charts. */}
                <div
                  className="relative pointer-events-none flex flex-col justify-around py-2"
                  style={{ gridColumn: '2 / -1', gridRow }}
                >
                  <div className="relative" style={{ height: SUB_BAR_HEIGHT_PX }}>
                    {lighter.width > 0 && (
                      <div
                        className={cn('absolute top-0 bottom-0 rounded', ENGAGEMENT_WINDOW_BG)}
                        style={{ left: `${lighter.left}%`, width: `${lighter.width}%` }}
                      />
                    )}
                    {darker.width > 0 && (
                      <AppLink
                        to={`/${row.engagementId}`}
                        title={
                          row.budgetedHoursLabel ?? (row.budgetedHoursPerDay !== undefined ? `${row.budgetedHoursPerDay}h/day` : undefined)
                            ? `${row.engagementName ?? row.engagementId} · ${row.budgetedHoursLabel ?? `${row.budgetedHoursPerDay}h/day`}`
                            : (row.engagementName ?? `Engagement ${row.engagementId}`)
                        }
                        className="absolute top-0 bottom-0 rounded bg-blue-500 hover:bg-blue-600 transition-colors pointer-events-auto"
                        style={{ left: `${darker.left}%`, width: `${darker.width}%` }}
                      >
                        <span className="sr-only">{row.engagementName ?? `Engagement ${row.engagementId}`}</span>
                      </AppLink>
                    )}
                  </div>
                </div>
              </Fragment>
            )
          })}
        </div>
      </div>

      <div className="mx-4 mb-4 pt-2 border-t border-dashed flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
          <span>{t?.workerActiveLegend || 'Worker active'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={cn('inline-block w-3 h-3 rounded-sm', ENGAGEMENT_WINDOW_BG)} />
          <span>{t?.engagementWindowLegend || 'Engagement window'}</span>
        </div>
      </div>
    </div>
  )
}
